import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Button, Card, CardBody, Input, Option, Select, Typography } from '@material-tailwind/react';
import { FaEye, FaFileInvoiceDollar, FaPen, FaSearch } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import LoadingPanel from './LoadingPanel';

const MONTH_OPTIONS = [
  { value: '', label: 'All Months' },
  ...Array.from({ length: 12 }, (_, index) => {
    const monthValue = index + 1;
    return {
      value: String(monthValue),
      label: new Date(2000, monthValue - 1, 1).toLocaleString('default', { month: 'long' })
    };
  })
];

const MonthlyInvoiceInvoicesReport = ({
  invoiceLoading,
  invoiceRows,
  invoiceTotal,
  invoicePage,
  setInvoicePage,
  invoicePages,
  invoiceStart,
  invoiceEnd,
  invoiceDateRangeLabel,
  draftFilters,
  setDraftFilters,
  ownerOptions,
  applyFilters,
  resetFilters,
  normalizeInvoiceStatus,
  formatDate,
  formatPeriod,
  statusBadgeClass,
  formatMoney
}) => {
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    if (!openMenu) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('[data-invoice-actions-menu="true"]')) {
        return;
      }

      if (target.closest('[data-invoice-actions-trigger="true"]')) {
        return;
      }

      setOpenMenu(null);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenMenu(null);
      }
    };

    const handleScrollOrResize = () => {
      setOpenMenu(null);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [openMenu]);

  const openInvoiceMenu = (invoice, triggerEl) => {
    const rect = triggerEl?.getBoundingClientRect?.();
    if (!rect) {
      setOpenMenu({ invoiceId: invoice.id, top: 0, left: 0, openUp: false });
      return;
    }

    const menuWidth = 176;
    const viewportPadding = 12;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceAbove > 220 && spaceAbove > spaceBelow;

    const left = Math.min(
      window.innerWidth - menuWidth - viewportPadding,
      Math.max(viewportPadding, rect.right - menuWidth)
    );

    setOpenMenu({
      invoiceId: invoice.id,
      top: openUp ? rect.top - 8 : rect.bottom + 8,
      left,
      openUp,
    });
  };

  const renderInvoiceActionsMenu = () => {
    if (!openMenu) return null;

    const invoice = invoiceRows.find((row) => row.id === openMenu.invoiceId);
    if (!invoice) return null;

    const status = normalizeInvoiceStatus(invoice);
    const openPath = `/owners/transactions/${encodeURIComponent(invoice.owner_code)}/${encodeURIComponent(invoice.id)}`;
    const editPath = `/owners/invoices/monthly/edit/${encodeURIComponent(invoice.owner_code)}/${encodeURIComponent(invoice.id)}`;

    return createPortal(
      <div
        data-invoice-actions-menu="true"
        className="fixed w-44 overflow-hidden rounded-md border border-slate-200 bg-white shadow-2xl"
        style={{
          top: `${openMenu.top}px`,
          left: `${openMenu.left}px`,
          transform: openMenu.openUp ? 'translateY(-100%)' : 'none',
          zIndex: 99999,
        }}
      >
        <Link
          to={openPath}
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
          onClick={() => setOpenMenu(null)}
        >
          <FaEye className="text-xs text-blue-600" />
          <span>Open</span>
        </Link>
        {status === 'DRAFT' && (
          <Link
            to={editPath}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            onClick={() => setOpenMenu(null)}
          >
            <FaPen className="text-xs text-orange-600" />
            <span>Edit</span>
          </Link>
        )}
      </div>,
      document.body
    );
  };

  return (
    <div className="space-y-4">
      <Card className="shadow-lg">
        <CardBody className="grid gap-4 lg:grid-cols-[1.2fr_1fr_1fr_1fr_auto]">
          <Input
            label="Search invoices"
            value={draftFilters.search}
            onChange={(e) => setDraftFilters((current) => ({ ...current, search: e.target.value }))}
            icon={<FaSearch />}
          />
          <Select
            label="Filter owner"
            value={draftFilters.owner_code}
            onChange={(value) => setDraftFilters((current) => ({ ...current, owner_code: value || '' }))}
          >
            <Option value="">All Owners</Option>
            {ownerOptions.map((option) => <Option key={option.value} value={option.value}>{option.label}</Option>)}
          </Select>
          <Select
            label="Filter month"
            value={draftFilters.month}
            onChange={(value) => setDraftFilters((current) => ({ ...current, month: value || '' }))}
          >
            {MONTH_OPTIONS.map((option) => (
              <Option key={option.value || 'all'} value={option.value}>{option.label}</Option>
            ))}
          </Select>
          <Select
            label="Filter status"
            value={draftFilters.status}
            onChange={(value) => setDraftFilters((current) => ({ ...current, status: value || '' }))}
          >
            <Option value="">All Statuses</Option>
            <Option value="DRAFT">Draft</Option>
            <Option value="SENT">Sent</Option>
            <Option value="PAID">Paid</Option>
          </Select>
          <div className="flex flex-wrap gap-2 self-end">
            <Button onClick={applyFilters} className="bg-orange-600 shadow-none">Apply</Button>
            <Button onClick={resetFilters} variant="outlined" className="border-slate-300 text-slate-700">Reset</Button>
          </div>
        </CardBody>
      </Card>

      <Card className="shadow-lg">
        <CardBody className="p-0">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <FaFileInvoiceDollar />Monthly account invoices
            </div>
            <Typography className="mt-1 text-xs text-slate-500">
              Paginated monthly invoices for {invoiceDateRangeLabel} loaded only when this tab is opened.
            </Typography>
          </div>
          {invoiceLoading ? <LoadingPanel label="Loading invoices..." /> : invoiceRows.length === 0 ? (
            <div className="px-5 py-14 text-center text-xs text-slate-500">No monthly invoices found for the selected filters.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-[1100px] w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Invoice</th>
                      <th className="px-4 py-3 text-left font-semibold">Owner</th>
                      <th className="px-4 py-3 text-left font-semibold">Status</th>
                      <th className="px-4 py-3 text-right font-semibold">Gross</th>
                      <th className="px-4 py-3 text-right font-semibold">Discount</th>
                      <th className="px-4 py-3 text-right font-semibold">Net</th>
                      <th className="px-4 py-3 text-center font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceRows.map((invoice) => {
                      const status = normalizeInvoiceStatus(invoice);
                      const openPath = `/owners/transactions/${encodeURIComponent(invoice.owner_code)}/${encodeURIComponent(invoice.id)}`;
                      const editPath = `/owners/invoices/monthly/edit/${encodeURIComponent(invoice.owner_code)}/${encodeURIComponent(invoice.id)}`;
                      const periodLabel = formatPeriod(invoice.date);
                      const invoiceNumber = String(invoice.id || '').split(':').pop() || invoice.id || '-';

                      return (
                        <tr key={invoice.id} className="border-t border-slate-200">
                          <td className="px-4 py-3">
                            <Link
                              to={status === 'DRAFT' ? editPath : openPath}
                              className="font-mono font-semibold text-orange-600 hover:underline"
                              title={`Period: ${periodLabel}`}
                            >
                              {invoiceNumber}
                            </Link>
                            <div className="mt-1 text-xs text-slate-500">{formatDate(invoice.date)}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-900">{invoice.owner_code || '-'}</div>
                            <div className="text-xs text-slate-500">{invoice.owner_name || '-'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass(status)}`}>{status}</span>
                          </td>
                          <td
                            className="px-4 py-3 text-right font-semibold text-slate-900"
                            title={`Cars: ${Number(invoice.car_count || 0)}`}
                          >
                            {formatMoney(invoice.gross_amount || 0)}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatMoney(invoice.discount_amount || 0)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatMoney(invoice.amount || 0)}</td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              size="sm"
                              variant="text"
                              onClick={(e) => {
                                if (openMenu?.invoiceId === invoice.id) {
                                  setOpenMenu(null);
                                } else {
                                  openInvoiceMenu(invoice, e.currentTarget);
                                }
                              }}
                              data-invoice-actions-trigger="true"
                              className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                              aria-label="Invoice actions"
                            >
                              <FontAwesomeIcon icon={faEllipsisV} className="text-sm" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-col gap-3 border-t border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-slate-600">Showing {invoiceStart}-{invoiceEnd} of {invoiceTotal} invoices</div>
                <div className="flex gap-2">
                  <Button
                    variant="outlined"
                    className="border-slate-300 text-slate-700"
                    disabled={invoicePage === 0}
                    onClick={() => setInvoicePage((page) => Math.max(0, page - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined"
                    className="border-slate-300 text-slate-700"
                    disabled={invoicePage >= invoicePages - 1}
                    onClick={() => setInvoicePage((page) => Math.min(invoicePages - 1, page + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
          {renderInvoiceActionsMenu()}
        </CardBody>
      </Card>
    </div>
  );
};

export default MonthlyInvoiceInvoicesReport;
